import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
// @mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Typography,
  Divider,
  Box,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
// @iconify
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
// utils
import {
  generateTokenId,
  generateTokenUid
} from 'src/utils/tokenUtils';
// validation schemas
import { tokenValidationSchema } from 'src/utils/validationSchemas';
// constants
import {
  TOKEN_FORM_INITIAL_VALUES,
  OCPI_COUNTRIES,
  OCPI_TOKEN_TYPES_ARRAY,
  OCPI_WHITELIST_TYPES_ARRAY,
  OCPI_PROFILE_TYPES_ARRAY,
  OCPI_LANGUAGES
} from 'src/utils/ocpi.constants';
import { Loader } from 'src/components/Loader';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 2,
    maxWidth: 800,
    width: '100%'
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2)
}));

const TokenForm = ({
  open,
  onClose,
  onSubmit,
  initialValues = null,
  isEdit = false
}) => {
  const formValues = initialValues || TOKEN_FORM_INITIAL_VALUES;

  const handleSubmit = (values, { setSubmitting }) => {
    // Process form data
    const tokenData = {
      ...values,
      visualNumber: values.visualNumber || null,
      groupId: values.groupId || null,
      energyContract: (values.energyContract?.supplierName || values.energyContract?.contractId)
        ? values.energyContract
        : null
    };
    onSubmit(tokenData, setSubmitting);
  };

  const handleGenerateTokenUid = (setFieldValue) => {
    const newUid = generateTokenUid();
    setFieldValue('uid', newUid);
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Formik
        initialValues={formValues}
        validationSchema={tokenValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => {
          return (
            <Form className='relative'>
              <Loader
                variant="top"
                open={isSubmitting}
                message="Creating Token..."
                description="Please wait while we process your request"
                showLinearProgress={isSubmitting}
              />
              <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6">
                    {isEdit ? 'Edit OCPI Token' : 'Create New OCPI Token'}
                  </Typography>
                  <IconButton onClick={onClose} size="small">
                    <Icon icon={closeFill} />
                  </IconButton>
                </Box>
              </DialogTitle>

              <DialogContent dividers>
                <Grid container spacing={3}>
                  {/* Basic Information Section */}
                  <Grid item xs={12}>
                    <SectionTitle variant="subtitle1">
                      Basic Information
                    </SectionTitle>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl fullWidth error={touched.countryCode && errors.countryCode}>
                      <InputLabel>Country Code *</InputLabel>
                      <Select
                        name="countryCode"
                        value={values.countryCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Country Code *"
                      >
                        {OCPI_COUNTRIES.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {country.code} - {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.countryCode && errors.countryCode && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.countryCode}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      name="partyId"
                      label="Party ID *"
                      value={values.partyId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.partyId && errors.partyId}
                      helperText={touched.partyId && errors.partyId}
                      inputProps={{ maxLength: 3 }}
                    />
                  </Grid>

                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      name="uid"
                      label="Token UID *"
                      value={values.uid}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.uid && errors.uid}
                      helperText={touched.uid && errors.uid}
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <Button
                      variant="outlined"
                      onClick={() => handleGenerateTokenUid(setFieldValue)}
                      fullWidth
                      sx={{ height: '56px' }}
                    >
                      Generate
                    </Button>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl fullWidth error={touched.type && errors.type}>
                      <InputLabel>Token Type *</InputLabel>
                      <Select
                        name="type"
                        value={values.type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Token Type *"
                      >
                        {OCPI_TOKEN_TYPES_ARRAY.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.type && errors.type && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.type}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl fullWidth error={touched.whitelist && errors.whitelist}>
                      <InputLabel>Whitelist Type *</InputLabel>
                      <Select
                        name="whitelist"
                        value={values.whitelist}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Whitelist Type *"
                      >
                        {OCPI_WHITELIST_TYPES_ARRAY.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.whitelist && errors.whitelist && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.whitelist}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="contractId"
                      label="Contract ID *"
                      value={values.contractId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.contractId && errors.contractId}
                      helperText={touched.contractId && errors.contractId}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="issuer"
                      label="Issuer *"
                      value={values.issuer}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.issuer && errors.issuer}
                      helperText={touched.issuer && errors.issuer}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="valid"
                          checked={values.valid}
                          onChange={(e) => {
                            handleChange({
                              target: {
                                name: 'valid',
                                value: e.target.checked
                              }
                            });
                          }}
                          color="success"
                        />
                      }
                      label="Token is valid"
                    />
                  </Grid>

                  {/* Optional Information Section */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <SectionTitle variant="subtitle1">
                      Optional Information
                    </SectionTitle>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      name="visualNumber"
                      label="Visual Number"
                      value={values.visualNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="1234-5678-9012"
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      name="groupId"
                      label="Group ID"
                      value={values.groupId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>Language</InputLabel>
                      <Select
                        name="language"
                        value={values.language}
                        onChange={handleChange}
                        label="Language"
                      >
                        {OCPI_LANGUAGES.map((lang) => (
                          <MenuItem key={lang.code} value={lang.code}>
                            {lang.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>Default Profile Type</InputLabel>
                      <Select
                        name="defaultProfileType"
                        value={values.defaultProfileType}
                        onChange={handleChange}
                        label="Default Profile Type"
                      >
                        {OCPI_PROFILE_TYPES_ARRAY.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Energy Contract Section */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Energy Contract
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      name="energyContract.supplierName"
                      label="Supplier Name"
                      value={values.energyContract?.supplierName || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      name="energyContract.contractId"
                      label="Contract ID"
                      value={values.energyContract?.contractId || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions sx={{ p: 3 }}>
                <Button onClick={onClose} color="inherit">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {isEdit ? 'Update Token' : 'Create Token'}
                </Button>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </StyledDialog>
  );
};

TokenForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  isEdit: PropTypes.bool
};

export default TokenForm;
